import { create } from "zustand";
import {
  createAssistantConversation,
  streamAssistantMessage,
  type AssistantToolEvent,
} from "../../../api/frameworksApi";
import { useAuthStore } from "../../../stores/authStore";
import type { ChatMessage } from "./types";
import { ENABLE_TYPING_PREVIEW } from "./lib/typingPreview";

export interface AssistantProgress {
  iteration: number;
  lastTool?: string;
  toolHistory: AssistantToolEvent[];
}

interface ComplianceAssistantState {
  open: boolean;
  minimized: boolean;
  messages: ChatMessage[];
  isThinking: boolean;
  progress: AssistantProgress | null;
  conversationId: string | null;
  conversationLoading: boolean;
  conversationError: string | null;

  openPanel: () => void;
  hidePanel: () => void;
  closePanel: () => void;
  minimizePanel: () => void;
  maximizePanel: () => void;
  resetConversation: () => void;
  sendMessage: (text: string) => Promise<void>;
  retry: (messageId: string) => Promise<void>;
  stopGeneration: () => void;
}

function buildConversationTitle(): string {
  const user = useAuthStore.getState().user;
  const who = user?.displayName || user?.username || user?.email || "Guest";
  const when = new Date().toLocaleString();
  return `${who} - ${when}`;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { errors?: Array<{ message?: string }>; message?: string } };
    message?: string;
  };
  const envelope = e?.response?.data;
  const first = envelope?.errors?.[0]?.message;
  if (first) return first;
  if (envelope?.message) return envelope.message;
  if (e?.message) return e.message;
  return fallback;
}

function friendlyAssistantError(raw: string | null | undefined): string {
  const text = (raw ?? "").trim();
  const lower = text.toLowerCase();

  if (/credit balance|billing|quota|insufficient|payment|purchase credit/.test(lower)) {
    return "The Compliance Companion is temporarily unavailable. Please try again later or contact your administrator.";
  }

  if (/rate limit|overloaded|too many requests|\b429\b/.test(lower)) {
    return "The assistant is busy right now. Please wait a moment and try again.";
  }

  if (/unauthorized|forbidden|api key|authentication|\b401\b|\b403\b/.test(lower)) {
    return "The assistant could not reach the service. Please contact your administrator.";
  }

  if (
    !text ||
    /anthropic|invalid_request_error|\{type=|"type"\s*:|bad request|\b[45]\d{2}\b/i.test(text)
  ) {
    return "Something went wrong while generating a response. Please try again.";
  }

  return text;
}

/**
 * The model frequently ends a reply with a "Would you like me to:" block - a
 * lead-in line followed by a bulleted list of next actions. When the backend also
 * returns Suggested Questions (rendered as tappable chips beneath the reply), that
 * block repeats the same guidance in a second format, so we drop it entirely and
 * let the chips be the single follow-up mechanism. When there are no Suggested
 * Questions we collapse the block to one short conversational sentence, so the
 * reply still nudges the user forward without the bulleted clutter.
 */
const FOLLOW_UP_LEAD_IN = /^would you like\b.*$/i;
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;
const HR_LINE = /^\s*([-*_])\1{2,}\s*$/;

function stripMarkers(line: string): string {
  return line
    .replace(/^\s*#{1,6}\s*/, "") // heading hashes
    .replace(/[*_`]/g, "") // bold / italic / inline-code markers
    .trim();
}

function joinActions(actions: string[]): string {
  if (actions.length === 1) return actions[0];
  return `${actions.slice(0, -1).join(", ")} or ${actions[actions.length - 1]}`;
}

function collapseFollowUpBlock(content: string, hasFollowUps: boolean): string {
  const lines = content.replace(/\r\n/g, "\n").split("\n");

  // Locate the lead-in line that is immediately followed (past blank lines) by a
  // list. Requiring the trailing list is what marks this as a follow-up-actions
  // block rather than ordinary prose, so the match never fires on a normal
  // "would you like ..." sentence in the body of an answer.
  let introIdx = -1;
  let listStart = -1;
  for (let i = 0; i < lines.length; i++) {
    if (!FOLLOW_UP_LEAD_IN.test(stripMarkers(lines[i]))) continue;
    let j = i + 1;
    while (j < lines.length && !lines[j].trim()) j++;
    if (j < lines.length && LIST_ITEM.test(lines[j])) {
      introIdx = i;
      listStart = j;
      break;
    }
  }
  if (introIdx === -1) return content;

  // Walk to the end of the contiguous list, collecting each action so any trailing
  // prose after the block is preserved rather than swallowed.
  let listEnd = listStart;
  const actions: string[] = [];
  while (listEnd < lines.length) {
    const m = lines[listEnd].match(LIST_ITEM);
    if (m) {
      actions.push(stripMarkers(m[1]).replace(/[.:;]+$/, "").trim());
      listEnd++;
    } else if (!lines[listEnd].trim()) {
      listEnd++;
    } else {
      break;
    }
  }

  // Trailing blank lines / dividers above the block would dangle once it is gone.
  const beforeLines = lines.slice(0, introIdx);
  while (
    beforeLines.length &&
    (!beforeLines[beforeLines.length - 1].trim() || HR_LINE.test(beforeLines[beforeLines.length - 1]))
  ) {
    beforeLines.pop();
  }
  const before = beforeLines.join("\n");
  const after = lines.slice(listEnd).join("\n").replace(/^\s+/, "");

  let middle = "";
  if (!hasFollowUps) {
    const stem = stripMarkers(lines[introIdx]).replace(/[:?]+\s*$/, "").trim();
    const picks = actions.filter(Boolean).slice(0, 3);
    // Only rebuild an inline sentence when the lead-in ends in "to", so the
    // lowercased action phrases read grammatically ("Would you like me to a, b or c?").
    if (picks.length && /\bto$/i.test(stem)) {
      const sentence = `${stem} ${joinActions(
        picks.map((a) => a.charAt(0).toLowerCase() + a.slice(1)),
      )}?`;
      middle =
        sentence.length <= 200
          ? sentence
          : "Let me know if you would like to explore any of these further.";
    } else {
      middle = "Let me know if you would like to explore any of these further.";
    }
  }

  return [before, middle, after].filter((s) => s.trim().length > 0).join("\n\n").trim();
}

let counter = 0;
const nextId = () => `ca-${++counter}`;

let pendingConversation: Promise<string | null> | null = null;

let activeStream: AbortController | null = null;

// Bumped whenever the user stops or resets. streamPrompt captures this at entry
// and bails if it changes across an await — this catches a Stop/reset during the
// first-message conversation-creation window, before any AbortController exists.
let generation = 0;

function abortActiveStream() {
  if (activeStream) {
    try {
      activeStream.abort();
    } catch {}
    activeStream = null;
  }
}

export const useComplianceAssistantStore = create<ComplianceAssistantState>((set, get) => {
  const ensureConversation = (): Promise<string | null> => {
    const existing = get().conversationId;
    if (existing) return Promise.resolve(existing);
    if (pendingConversation) return pendingConversation;

    set({ conversationLoading: true, conversationError: null });
    pendingConversation = createAssistantConversation(buildConversationTitle())
      .then((conv) => {
        set({ conversationId: conv.id, conversationLoading: false });
        return conv.id;
      })
      .catch((err: unknown) => {
        const message = extractErrorMessage(err, "Could not start a conversation.");
        set({ conversationLoading: false, conversationError: message });
        return null;
      })
      .finally(() => {
        pendingConversation = null;
      });

    return pendingConversation;
  };

  const streamPrompt = async (prompt: string) => {
    const myGen = generation;
    const convId = await ensureConversation();
    // Stop/reset during conversation creation (before a controller exists) can't
    // be caught by abortActiveStream — honor it here so the reply isn't appended.
    if (generation !== myGen) return;
    if (!convId) {
      set((s) => ({
        isThinking: false,
        progress: null,
        messages: [
          ...s.messages,
          {
            id: nextId(),
            role: "assistant",
            error: true,
            text: friendlyAssistantError(
              get().conversationError ?? "Could not start a conversation. Please try again.",
            ),
            sourcePrompt: prompt,
            createdAt: Date.now(),
          },
        ],
      }));
      return;
    }

    abortActiveStream();
    const controller = new AbortController();
    activeStream = controller;

    type Terminal =
      | { kind: "assistant"; id: string; text: string; followUps?: string[]; createdAt: number }
      | { kind: "error"; text: string };
    let terminal: Terminal | null = null;
    let aborted = false;

    try {
      await streamAssistantMessage(
        convId,
        prompt,
        {
          onIterationStart: ({ iteration }) => {
            set((s) => ({
              progress: {
                iteration,
                lastTool: s.progress?.lastTool,
                toolHistory: s.progress?.toolHistory ?? [],
              },
            }));
          },
          onToolDispatched: (evt) => {
            set((s) => ({
              progress: {
                iteration: s.progress?.iteration ?? 1,
                lastTool: evt.toolName,
                toolHistory: [...(s.progress?.toolHistory ?? []), evt],
              },
            }));
          },
          onAssistant: ({ message, stopReason, suggestedQuestions }) => {
            const hasFollowUps = !!suggestedQuestions?.length;
            // Suggested Questions are the primary follow-up mechanism; drop the
            // redundant "Would you like me to:" block (or collapse it to one line
            // when no questions were returned) so guidance is never duplicated.
            const body = collapseFollowUpBlock(message?.content ?? "", hasFollowUps);
            const truncated = stopReason === "MAX_TOKENS";
            const text = truncated
              ? `${body}\n\n*(Reply was cut off because the model hit its output limit. Ask a more specific follow-up to continue.)*`
              : body;
            terminal = {
              kind: "assistant",
              id: message?.id || nextId(),
              text,
              followUps: hasFollowUps ? suggestedQuestions : undefined,
              createdAt: Date.parse(message?.createdAt ?? "") || Date.now(),
            };
          },
          onError: ({ message }) => {
            terminal = { kind: "error", text: friendlyAssistantError(message) };
          },
          onDone: () => {},
        },
        controller.signal,
      );
    } catch (err: unknown) {
      if (controller.signal.aborted) {
        aborted = true;
      } else {
        terminal = {
          kind: "error",
          text: friendlyAssistantError(
            extractErrorMessage(err, "Something went wrong while generating a response."),
          ),
        };
      }
    }

    if (aborted) {
      if (activeStream === controller) activeStream = null;
      return;
    }

    // `terminal` is only ever assigned inside the stream callbacks above, which
    // TS's control-flow analysis cannot see — it narrows the variable to the
    // catch-block/null branch and drops the "assistant" case. Read it back as the
    // full declared union so both variants survive.
    const outcome: Terminal =
      (terminal as Terminal | null) ?? {
        kind: "error",
        text: "The assistant did not return a response. Please try again.",
      };

    if (activeStream === controller) activeStream = null;
    if (controller.signal.aborted) return;

    if (outcome.kind === "assistant") {
      set((s) => ({
        isThinking: false,
        progress: null,
        messages: [
          ...s.messages,
          {
            id: outcome.id,
            role: "assistant",
            text: outcome.text,
            followUps: outcome.followUps,
            sourcePrompt: prompt,
            createdAt: outcome.createdAt,
            animate: ENABLE_TYPING_PREVIEW,
          },
        ],
      }));
    } else {
      set((s) => ({
        isThinking: false,
        progress: null,
        messages: [
          ...s.messages,
          {
            id: nextId(),
            role: "assistant",
            error: true,
            text: outcome.text,
            sourcePrompt: prompt,
            createdAt: Date.now(),
          },
        ],
      }));
    }
  };

  return {
    open: false,
    minimized: false,
    messages: [],
    isThinking: false,
    progress: null,
    conversationId: null,
    conversationLoading: false,
    conversationError: null,

    openPanel: () => {
      // Open into the full workspace by default; the user can collapse it from
      // the header ("Compact") whenever they want the small floating card.
      set({ open: true, minimized: false });
      void ensureConversation();
    },

    hidePanel: () => set({ open: false }),

    closePanel: () => set({ open: false }),

    minimizePanel: () =>
      set((s) => (s.open && !s.minimized ? { minimized: true } : s)),

    maximizePanel: () => set((s) => (s.minimized ? { minimized: false } : s)),

    resetConversation: () => {
      generation++;
      abortActiveStream();
      pendingConversation = null;
      set({
        messages: [],
        isThinking: false,
        progress: null,
        conversationId: null,
        conversationLoading: false,
        conversationError: null,
      });
      void ensureConversation();
    },

    sendMessage: async (text) => {
      const prompt = text.trim();
      if (!prompt || get().isThinking) return;
      set((s) => ({
        messages: [
          ...s.messages,
          { id: nextId(), role: "user", text: prompt, createdAt: Date.now() },
        ],
        isThinking: true,
        progress: null,
      }));
      await streamPrompt(prompt);
    },

    retry: async (messageId) => {
      const msg = get().messages.find((m) => m.id === messageId);
      if (!msg?.sourcePrompt || get().isThinking) return;
      set((s) => ({
        messages: s.messages.filter((m) => m.id !== messageId),
        isThinking: true,
        progress: null,
      }));
      await streamPrompt(msg.sourcePrompt);
    },

    stopGeneration: () => {
      if (!get().isThinking) return;
      generation++;
      abortActiveStream();
      set({ isThinking: false, progress: null });
    },
  };
});
